import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { fileToBase64 } from "@/lib/format";
import ZButton from "@/components/ZButton";
import { ZInput } from "@/components/ZInput";
import { BadgeCheck, ShieldCheck, Clock, XCircle, Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";

const ELIGIBLE = ["association", "entreprise", "institution"];

export default function OrgVerificationCard({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ rccm: "", nif: "" });
  const [docs, setDocs] = useState([]); // {name, mime, data_b64}
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const eligible = ELIGIBLE.includes(user.org_type);

  const load = () => {
    api.get("/organizer/verification")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onFiles = async (e) => {
    setErr("");
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (docs.length >= 4) { setErr("Maximum 4 documents."); break; }
      if (f.size > 3_000_000) { setErr(`« ${f.name} » dépasse 3 Mo.`); continue; }
      if (!f.type.startsWith("image/") && f.type !== "application/pdf") { setErr("Formats acceptés : image ou PDF."); continue; }
      const b64 = await fileToBase64(f);
      setDocs((d) => [...d, { name: f.name, mime: f.type, data_b64: b64 }]);
    }
    e.target.value = "";
  };

  const removeDoc = (i) => setDocs((d) => d.filter((_, idx) => idx !== i));

  const submit = async () => {
    setErr("");
    if (form.rccm.trim().length < 2) return setErr("Numéro RCCM obligatoire.");
    if (form.nif.trim().length < 2) return setErr("Numéro NIF obligatoire.");
    setSubmitting(true);
    try {
      const r = await api.post("/organizer/verification", {
        rccm: form.rccm.trim(), nif: form.nif.trim(), documents: docs,
      });
      setData(r.data);
      setShowForm(false);
      toast.success("Demande de certification envoyée.");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  const verified = data?.verified || user?.verified;
  const status = data?.status || "none";

  // Already certified
  if (verified) {
    return (
      <div className="z-card" style={{ padding: 20, marginTop: 18, borderLeft: "4px solid var(--z-teal)" }} data-testid="org-verif-card">
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--z-teal)", fontWeight: 700 }}>
          <BadgeCheck size={22} /> Organisateur certifié
        </div>
        <p style={{ margin: "8px 0 0", fontSize: 13.5, color: "var(--z-muted)" }}>
          Le badge ✔️ apparaît à côté de votre nom sur toutes vos pages publiques. Bravo !
        </p>
      </div>
    );
  }

  return (
    <div className="z-card" style={{ padding: 20, marginTop: 18 }} data-testid="org-verif-card">
      <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700 }}>
        <ShieldCheck size={20} color="var(--z-teal)" /> Certification organisateur
      </div>

      {!eligible ? (
        <p style={{ margin: "10px 0 0", fontSize: 13.5, color: "var(--z-muted)" }} data-testid="org-verif-ineligible">
          La certification est réservée aux <strong>entreprises, associations et institutions</strong>. Mettez à jour votre type d'organisation pour devenir éligible.
        </p>
      ) : status === "pending" ? (
        <div style={{ marginTop: 10 }} data-testid="org-verif-pending">
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(218,113,1,0.12)", color: "var(--z-orange)", padding: "4px 10px", borderRadius: 999, fontSize: 13, fontWeight: 700 }}>
            <Clock size={14} /> Demande en cours d'examen
          </div>
          <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--z-muted)" }}>
            Notre équipe vérifie vos informations. Vous recevrez un e-mail dès validation.
          </p>
        </div>
      ) : (
        <>
          <p style={{ margin: "10px 0 12px", fontSize: 13.5, color: "var(--z-muted)" }}>
            Renforcez la confiance des participants avec le badge ✔️ « Certifié ». Fournissez vos informations légales pour vérification.
          </p>
          {status === "rejected" && data?.reject_reason && (
            <div style={{ background: "rgba(161,44,123,0.08)", color: "var(--z-error)", padding: 10, borderRadius: 10, fontSize: 13, marginBottom: 12, display: "flex", gap: 8 }} data-testid="org-verif-rejected">
              <XCircle size={16} style={{ flexShrink: 0 }} /> Demande refusée : {data.reject_reason}
            </div>
          )}

          {!showForm ? (
            <ZButton onClick={() => setShowForm(true)} data-testid="org-verif-start-btn">
              {status === "rejected" ? "Soumettre une nouvelle demande" : "Demander la certification"}
            </ZButton>
          ) : (
            <div data-testid="org-verif-form">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <ZInput label="Numéro RCCM" value={form.rccm} onChange={(e) => setForm({ ...form, rccm: e.target.value })} placeholder="GN-CKY-2024-B-12345" data-testid="org-verif-rccm" />
                <ZInput label="Numéro NIF" value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} placeholder="123456789" data-testid="org-verif-nif" />
              </div>

              <label className="z-label">Justificatifs (CNI/passeport + registre) — image ou PDF, max 3 Mo</label>
              <label className="z-btn z-btn-outline z-btn-sm" style={{ cursor: "pointer", marginBottom: 10 }} data-testid="org-verif-upload-label">
                <Upload size={14} /> Ajouter un document
                <input type="file" accept="image/*,application/pdf" multiple onChange={onFiles} style={{ display: "none" }} data-testid="org-verif-file-input" />
              </label>
              {docs.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                  {docs.map((d, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, background: "var(--z-bg-soft,#f6f6f6)", padding: "6px 10px", borderRadius: 8 }} data-testid={`org-verif-doc-${i}`}>
                      <FileText size={14} color="var(--z-teal)" />
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                      <button type="button" onClick={() => removeDoc(i)} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error)" }} data-testid={`org-verif-doc-remove-${i}`}><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}

              {err && <p className="z-error-text" data-testid="org-verif-error">{err}</p>}
              <div style={{ display: "flex", gap: 10 }}>
                <ZButton variant="outline" block onClick={() => setShowForm(false)} data-testid="org-verif-cancel">Annuler</ZButton>
                <ZButton block loading={submitting} onClick={submit} data-testid="org-verif-submit">Envoyer la demande</ZButton>
              </div>
              <div style={{ height: 80 }} aria-hidden="true" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
